import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthComponents';
import { apiRequest } from '../utils/api';
import { toast } from 'react-toastify';
import { User, Lock, Save, Edit, Eye, EyeOff, Building, Mail, Shield, Scale, ExternalLink } from 'lucide-react';
import { normalizeRole, roleLabel, isLawyerRole } from '../utils/userPermissions';

const ProfilePage = ({ setCurrentPage }) => {
  const { user, updateUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [lawyerProfile, setLawyerProfile] = useState(null);
  const [lawyerLoading, setLawyerLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    company_name: '',
    email: '',
  });
  const [companyLocked, setCompanyLocked] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isBusinessAccount = normalizeRole(user?.role) === 'business';
  const isLawyerAccount = isLawyerRole(user?.role);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'lawyer' && !isLawyerAccount) {
      setActiveTab('overview');
      return;
    }
    if (tab) setActiveTab(tab);
  }, [searchParams, isLawyerAccount]);

  useEffect(() => {
    if (activeTab === 'lawyer' && !isLawyerAccount) {
      setActiveTab('overview');
    }
  }, [activeTab, isLawyerAccount]);

  useEffect(() => {
    if (activeTab === 'lawyer' && isLawyerAccount) {
      fetchLawyerProfile();
    }
  }, [activeTab, isLawyerAccount]);

  const fetchLawyerProfile = async () => {
    setLawyerLoading(true);
    try {
      const data = await apiRequest('lawyers/me');
      setLawyerProfile(data || null);
    } catch {
      setLawyerProfile(null);
    } finally {
      setLawyerLoading(false);
    }
  };

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('users/profile');
      if (data) {
        setProfileData({
          full_name: data.full_name || '',
          company_name: data.company_name || '',
          email: data.email || ''
        });
        setCompanyLocked(Boolean(data.company_name_locked || (data.company_name || '').trim()));
        updateUser(data); // Update context with fresh data
      }
    } catch (err) {
      setError(err.message);
      toast.error('Failed to load profile data.');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const updatedUser = await apiRequest('users/profile', {
        method: 'PUT',
        body: {
          full_name: profileData.full_name,
          company_name: profileData.company_name,
        },
      });
      updateUser(updatedUser);
      toast.success('Profile updated successfully!');
      setActiveTab('overview');
    } catch (err) {
      setError(err.message);
      toast.error(`Failed to update profile: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match.');
      toast.error('New passwords do not match.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest('users/change-password', {
        method: 'POST',
        body: passwordData,
      });
      toast.success(response.message);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setActiveTab('overview');
    } catch (err) {
      setError(err.message);
      toast.error(`Failed to change password: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !profileData.email) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--color-text-secondary)]">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-4">
      <div className="max-w-full mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)] mb-4 leading-tight">
            Profile{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
              Settings
            </span>
          </h1>
          <p className="text-lg text-[var(--color-text-muted)]">
            Manage your account information and preferences.
          </p>
          </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-900/20 border border-red-500/40 text-red-300">
            <div className="flex items-center">
              <Shield className="w-5 h-5 mr-3" />
              <span>{error}</span>
            </div>
          </div>
        )}
        
        <div className="elevated-card backdrop-blur-sm border border-[var(--color-border)] rounded-3xl p-6">
          {/* Tab Navigation */}
          {activeTab !== 'edit-profile' ? (
            <div className="flex overflow-x-auto scrollbar-thin border-b border-[var(--color-border)] mb-6 -mx-1 px-1">
              <button
                onClick={() => setActiveTab('overview')}
                className={`shrink-0 px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'overview'
                    ? 'border-b-2 border-[var(--color-primary)] text-[var(--color-text)]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }`}
              >
                Overview
              </button>
              {isLawyerAccount ? (
                <button
                  onClick={() => setActiveTab('lawyer')}
                  className={`shrink-0 px-4 py-2 text-sm font-medium transition-colors ${
                    activeTab === 'lawyer'
                      ? 'border-b-2 border-[var(--color-primary)] text-[var(--color-text)]'
                      : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                  }`}
                >
                  Lawyer Profile
                </button>
              ) : null}
              <button
                onClick={() => setActiveTab('security')}
                className={`shrink-0 px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'security'
                    ? 'border-b-2 border-[var(--color-primary)] text-[var(--color-text)]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }`}
              >
                Security
              </button>
            </div>
          ) : null}

          {/* Tab Content */}
            <div>
            {activeTab === 'overview' && (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-4">Account Overview</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="elevated-card p-4 rounded-xl">
                    <label className="block text-sm font-medium text-[var(--color-text-muted)]">Full Name</label>
                    <p className="text-[var(--color-text)]">{profileData.full_name}</p>
                  </div>
                  <div className="elevated-card p-4 rounded-xl">
                    <label className="block text-sm font-medium text-[var(--color-text-muted)]">Email Address</label>
                    <p className="text-[var(--color-text)]">{profileData.email}</p>
                  </div>
                  {isBusinessAccount ? (
                    <div className="elevated-card p-4 rounded-xl">
                      <label className="block text-sm font-medium text-[var(--color-text-muted)]">Company Name</label>
                      <p className="text-[var(--color-text)]">{profileData.company_name || 'N/A'}</p>
                    </div>
                  ) : null}
                  <div className="elevated-card p-4 rounded-xl">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <label className="block text-sm font-medium text-[var(--color-text-muted)]">Account Type</label>
                        <p className="text-[var(--color-text)]">{roleLabel(user?.role)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveTab('edit-profile')}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[var(--color-primary)]/35 bg-[var(--color-primary-soft)] px-3 py-1.5 text-xs font-semibold text-[var(--color-primary)] transition-colors hover:bg-[var(--color-primary-hover)]/20"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        Edit Profile
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'edit-profile' && (
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <button
                  type="button"
                  onClick={() => setActiveTab('overview')}
                  className="inline-flex items-center gap-2 text-sm text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text)]"
                >
                  <span aria-hidden="true">←</span>
                  Back to overview
                </button>
                <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-4">Edit Profile Information</h2>
              <div className="space-y-4">
                      <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2" htmlFor="full_name">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                      <input
                        type="text"
                        name="full_name"
                        id="full_name"
                        value={profileData.full_name}
                        onChange={handleProfileChange}
                        className="w-full pl-12 pr-4 py-3 themed-input border border-[var(--color-border)]/40 rounded-xl text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]/50 focus:border-[var(--color-primary)]/50 transition-all"
                        placeholder="Your full name"
                      />
                    </div>
                  </div>
                  {isBusinessAccount ? (
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2" htmlFor="company_name">
                        Company Name
                      </label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                        <input
                          type="text"
                          name="company_name"
                          id="company_name"
                          value={profileData.company_name}
                          onChange={handleProfileChange}
                          disabled={companyLocked}
                          className="w-full pl-12 pr-4 py-3 themed-input border border-[var(--color-border)]/40 rounded-xl text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]/50 focus:border-[var(--color-primary)]/50 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                          placeholder="Your company name"
                        />
                      </div>
                      {companyLocked ? (
                        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
                          Business name can’t be changed once set. Contact support if this was a mistake.
                        </p>
                      ) : (
                        <p className="mt-2 text-xs text-amber-400/90">
                          Required for Hiring Shortlist. Once saved, this name becomes permanent.
                        </p>
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="flex justify-end">
                  <button type="submit" disabled={loading} className="brand-gradient-btn px-6 py-2 rounded-lg font-medium transition-all disabled:opacity-50">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
          )}

            {activeTab === 'security' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-4">Change Password</h2>
              <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2" htmlFor="current_password">
                      Current Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
                  <input
                        type={showPassword ? 'text' : 'password'}
                        name="current_password"
                        id="current_password"
                        value={passwordData.current_password}
                        onChange={handlePasswordChange}
                        className="w-full pl-12 pr-12 py-3 themed-input border border-[var(--color-border)]/40 rounded-xl text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]/50 focus:border-[var(--color-primary)]/50 transition-all"
                        placeholder="Your current password"
                      />
            <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
          </div>
      </div>
            <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2" htmlFor="new_password">
                      New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
            <input
                        type={showPassword ? 'text' : 'password'}
                        name="new_password"
                        id="new_password"
                        value={passwordData.new_password}
                        onChange={handlePasswordChange}
                        className="w-full pl-12 pr-12 py-3 themed-input border border-[var(--color-border)]/40 rounded-xl text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]/50 focus:border-[var(--color-primary)]/50 transition-all"
                        placeholder="Your new password"
            />
          </div>
          </div>
          <div>
                    <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2" htmlFor="confirm_password">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-text-muted)]" />
            <input
                        type={showPassword ? 'text' : 'password'}
                        name="confirm_password"
                        id="confirm_password"
                        value={passwordData.confirm_password}
                        onChange={handlePasswordChange}
                        className="w-full pl-12 pr-12 py-3 themed-input border border-[var(--color-border)]/40 rounded-xl text-[var(--color-text)] placeholder-[var(--color-placeholder)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]/50 focus:border-[var(--color-primary)]/50 transition-all"
                        placeholder="Confirm your new password"
                      />
                    </div>
        </div>
        </div>
                <div className="flex justify-end">
                  <button type="submit" disabled={loading} className="brand-gradient-btn px-6 py-2 rounded-lg font-medium transition-all disabled:opacity-50">
                    {loading ? 'Updating...' : 'Update Password'}
            </button>
                </div>
              </form>
            )}

            {isLawyerAccount && activeTab === 'lawyer' && (
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2 flex items-center gap-2">
                  <Scale className="w-6 h-6 text-[var(--color-primary)]" />
                  Lawyer Profile
                </h2>
                {lawyerLoading ? (
                  <p className="text-[var(--color-text-muted)]">Loading…</p>
                ) : !lawyerProfile ? (
                  <div className="rounded-xl border border-dashed border-[var(--color-border)] p-8 text-center">
                    <p className="text-[var(--color-text-secondary)] mb-4">You have not submitted a lawyer profile yet.</p>
                    <Link to="/become-a-lawyer" className="inline-flex items-center gap-2 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] px-5 py-2.5 rounded-lg font-medium">
                      Create lawyer profile
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`text-xs font-semibold uppercase px-3 py-1 rounded-full ${
                        lawyerProfile.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                        lawyerProfile.status === 'pending' ? 'bg-amber-500/20 text-amber-200' :
                        lawyerProfile.status === 'rejected' ? 'bg-red-500/20 text-red-300' :
                        'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]'
                      }`}>
                        {lawyerProfile.status}
                      </span>
                      {lawyerProfile.status === 'approved' && lawyerProfile.profile_url ? (
                        <a href={lawyerProfile.profile_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:text-[var(--color-primary)] text-sm">
                          Public profile <ExternalLink className="w-4 h-4" />
                        </a>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="elevated-card p-4 rounded-xl">
                        <label className="block text-sm text-[var(--color-text-muted)]">Display name</label>
                        <p className="text-[var(--color-text)]">{lawyerProfile.display_name}</p>
                      </div>
                      <div className="elevated-card p-4 rounded-xl">
                        <label className="block text-sm text-[var(--color-text-muted)]">Headline</label>
                        <p className="text-[var(--color-text)]">{lawyerProfile.headline}</p>
                      </div>
                    </div>
                    {lawyerProfile.rejection_reason ? (
                      <div className="rounded-xl bg-red-900/20 border border-red-500/30 p-4 text-red-200 text-sm">
                        <strong>Reviewer note:</strong> {lawyerProfile.rejection_reason}
                      </div>
                    ) : null}
                    <p className="text-[var(--color-text-muted)] text-sm">
                      {lawyerProfile.status === 'approved'
                        ? 'Updates to your listing go live immediately after you save changes.'
                        : 'We will email you when your profile is approved.'}
                    </p>
                    <Link
                      to="/become-a-lawyer"
                      className="inline-flex items-center gap-2 border border-[var(--color-primary)]/40 text-[var(--color-primary)] hover:bg-[var(--color-primary-hover)]/10 px-5 py-2.5 rounded-lg font-medium"
                    >
                      {lawyerProfile.status === 'approved' ? 'Edit listing' : 'Update & resubmit'}
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
